// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { Alert, AppIcon, Button as FinButton, Button, Modal as FinModal, TablePagination, SearchBar, Spinner } from '@loa/ui-components';
import { useAdvisor } from '../contexts/AdvisorContext';
import { ExecutiveSummaryCard } from '../components/executivesummary/ExecutiveSummaryCard';
import { SuggestedStepsPanel } from '../components/suggestions/SuggestedStepsPanel';
import { OpenItemsPanel } from '../components/openitems/OpenItemsPanel';
import { CashflowForecastTab } from '../components/cashflow/CashflowForecastTab';
import { featureFlags } from '../config/featureFlags';
import { relationshipApi } from '../api/relationshipApi';
import { dashboardApi } from '../api/dashboardApi';
import type { DashboardAlert } from '../types/dashboard';
import { clientApi } from '../api/clientApi';
import { policyApi } from '../api/policyApi';
import { invitationApi } from '../api/invitationApi';
import { supportApi } from '../api/supportApi';
import { RelationshipEditModal } from '../components/RelationshipEditModal';
import { ArchiveRelationshipModal } from '../components/ArchiveRelationshipModal';
import { ArchiveClientModal } from '../components/ArchiveClientModal';
import { Card } from '../components/Card';
import { ClientList } from '../components/ClientList';
import { ClientForm } from '../components/ClientForm';
import { PolicyList } from '../components/PolicyList';
import { PolicyForm } from '../components/PolicyForm';
import { DeletePolicyModal } from '../components/policy/DeletePolicyModal';
import { PolicySignatories } from '../components/PolicySignatories';
import { SendInvitationModal } from '../components/SendInvitationModal';
import { ClientSignedDocumentsList } from '../components/ClientSignedDocumentsList';
import { RelationshipEmailsCard } from '../components/relationship/RelationshipEmailsCard';
import { CopyTagButton } from '../components/integrations/CopyTagButton';
import { ClientActivityTab } from '../components/client/ClientActivityTab';
import { RelationshipNotesTab } from '../components/relationship/RelationshipNotesTab';
import { FactFindAccordion } from '../components/profileform/FactFindAccordion';
import { FinancialPositionTab } from '../components/financialposition/FinancialPositionTab';
import { TaxClassificationPanel } from '../components/financialposition/TaxClassificationPanel';
import { InheritancesPanel } from '../components/financialposition/InheritancesPanel';
import { FinancialGoalsPanel } from '../components/financialposition/FinancialGoalsPanel';
import { FinancialDashboard } from '../components/financialposition/FinancialDashboard';
import { RelationshipCashflowCard } from '../components/financialposition/RelationshipCashflowCard';
import { RelationshipAssumptionsDialog } from '../components/financialposition/RelationshipAssumptionsDialog';
import { RequestsTab } from '../components/requests/RequestsTab';
import { ProposalsTab } from '../components/proposals/ProposalsTab';
// PendingActionsCard removed — warnings now shown only in Alerts section on Overview tab
import ReassignRelationshipModal from '../components/ReassignRelationshipModal';
import { BulkPublishConfirmModal } from '../components/signatoryStatus';
import { useSignatoryStatusSummary } from '../hooks/useSignatoryStatusSummary';
import { getProfileOverrides, deleteOverride } from '../api/policyOverrideApi';
import type { ProfileOverrideSummaryDTO, OverrideSummaryItem } from '../types/policyOverride';
import type { Relationship, RelationshipUpdateDTO, RelationshipType } from '../types/relationship';
import { usePageContextRegister } from '../contexts/PageContextProvider';
import type { Client, ClientCreateDTO, ClientUpdateDTO } from '../types/client';
import type { Policy, PolicyCreateDTO, PolicyUpdateDTO } from '../types/policy';
import { getTenant } from '../utils/auth';
import { EntityTagEditor } from '../components/tags/EntityTagEditor';
import { useEntityTags } from '../hooks/useEntityTags';
import './RelationshipDetailPage.css';
import { TabBar } from '../components/TabBar';
import { RelationshipActionMenu } from '../components/RelationshipActionMenu';
import { ToolsMenu, type ToolKey } from '../components/relationship/tools/ToolsMenu';
import { RelationshipToolsTab } from '../components/relationship/tools/RelationshipToolsTab';
import {
  UnsavedChangesProvider,
  useUnsavedChangesOptional,
} from '../components/relationship/tools/maxfunding/UnsavedChangesContext';

const COA_ENABLED_TENANTS = new Set(['tpf', 'e2e-adv-test']);
type TabType = 'overview' | 'clients' | 'policies' | 'signed-documents' | 'requests' | 'proposals' | 'fact-find' | 'financial-position' | 'cashflow' | 'notes' | 'activity' | 'tools';

/**
 * Legacy tab-param value kept for backwards compatibility with bookmarks,
 * deep-links in notifications, and external emails that still say
 * {@code ?tab=loas}. Normalised to {@code 'signed-documents'} on read.
 */
const LEGACY_LOAS_TAB = 'loas';

/**
 * Legacy tab-param for the former top-level Quotes tab. The standalone quote
 * generator has been retired — quotes now live inside proposals. Old
 * {@code ?tab=quotes} links resolve to the Tools tab and are rewritten on mount.
 */
const LEGACY_QUOTES_TAB = 'quotes';

export function RelationshipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbTitle, setBreadcrumbItems, setHideBreadcrumbBack } = usePageHeader();
  const { advisor: currentAdvisor, isSupport, isAdmin } = useAdvisor();
  const [relationship, setRelationship] = useState<Relationship | null>(null);
  usePageContextRegister({
    pageType: 'RELATIONSHIP_DETAIL',
    entityIds: id ? { profileId: id } : undefined,
    pageTitle: relationship?.profileName,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);

  // Tag chips + picker on the relationship header (Tags PRD §9). A single
  // id → single-entity batch call; renders nothing when the feature is off.
  const { tagsByEntityId } = useEntityTags('RELATIONSHIP', id ? [id] : []);
  const relationshipTags = id ? tagsByEntityId[id] ?? [] : [];
  // Shared refresh for the Financial Position cards: an income change in the
  // dashboard refetches both the tiles AND the cashflow projection.
  const [fpRefresh, setFpRefresh] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS: TabType[] = ['overview', 'clients', 'policies', 'signed-documents', 'requests', 'proposals', 'fact-find', 'financial-position', ...(featureFlags.cashflowWorkspace ? (['cashflow'] as TabType[]) : []), 'notes', 'activity', 'tools'];
  const rawTabParam = searchParams.get('tab');
  let tabParam: TabType;
  if (rawTabParam === LEGACY_LOAS_TAB) tabParam = 'signed-documents';
  else if (rawTabParam === LEGACY_QUOTES_TAB) tabParam = 'tools';
  else tabParam = rawTabParam as TabType;
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'overview';

  const setActiveTab = (tab: TabType) => {
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
  };

  // The standalone quote generator has been retired (quotes now live inside
  // proposals). Rewrite the legacy `?tab=quotes` deep link to the Tools tab.
  useEffect(() => {
    if (searchParams.get('tab') === LEGACY_QUOTES_TAB) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'tools');
      next.delete('tool');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [overviewMode, setOverviewMode] = useState<'alerts' | 'insights'>('alerts');
  const [profileAlerts, setProfileAlerts] = useState<DashboardAlert[]>([]);

  // Client management states
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsTotalPages, setClientsTotalPages] = useState(0);
  const [clientsCurrentPage, setClientsCurrentPage] = useState(0);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [submittingClient, setSubmittingClient] = useState(false);
  const [archiveClientConfirm, setArchiveClientConfirm] = useState<{
    open: boolean;
    client: Client | null;
  }>({ open: false, client: null });
  const [archivingClient, setArchivingClient] = useState(false);

  // Policy management states
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [policiesTotalPages, setPoliciesTotalPages] = useState(0);
  const [policiesTotalElements, setPoliciesTotalElements] = useState(0);
  const [policiesCurrentPage, setPoliciesCurrentPage] = useState(0);
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [policiesError, setPoliciesError] = useState<string | null>(null);
  const [policySearchQuery, setPolicySearchQuery] = useState('');
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [submittingPolicy, setSubmittingPolicy] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [showSignatoriesModal, setShowSignatoriesModal] = useState(false);
  const [deletingPolicyId, setDeletingPolicyId] = useState<string | null>(null);

  // Invitation management states. The fetched summary, loading flag and
  // error message used to be tracked in useState pairs but the values were
  // never read (the existing UI only triggers the side-effect fetch); drop
  // them to satisfy S6754 and just call the API for its side effects.
  const [showBulkInvitationModal, setShowBulkInvitationModal] = useState(false);

  // LOA management states
  const [loaCount, setLoaCount] = useState(0);
  const [requestsCount, setRequestsCount] = useState(0);
  const [proposalsCount, setProposalsCount] = useState(0);

  // Reassignment modal state
  const [showReassignModal, setShowReassignModal] = useState(false);

  // Relationship aggregate is fetched primarily for its side effect on
  // `loaCount` — we don't render the aggregate object itself yet, so we
  // skip storing it. (Drop write-only useState pairs to satisfy S6754.)

  // Signatory status state
  const { data: signatoryStatusSummary, refresh: refreshSignatoryStatus } = useSignatoryStatusSummary(id || '');
  const [showBulkPublishModal, setShowBulkPublishModal] = useState(false);

  // Profile overrides state
  const [profileOverrides, setProfileOverrides] = useState<ProfileOverrideSummaryDTO | null>(null);
  const [removeOverrideConfirm, setRemoveOverrideConfirm] = useState<OverrideSummaryItem | null>(null);

  useEffect(() => {
    const name = relationship?.relationshipName || relationship?.profileName || null;
    setBreadcrumbTitle(name);

    const tool = searchParams.get('tool');
    const view = searchParams.get('view');
    const inMaxFundingScenario =
      activeTab === 'tools' && tool === 'max-funding' && (view === 'scenario' || view === 'new');

    if (activeTab === 'tools' && tool === 'max-funding' && name && id) {
      setBreadcrumbItems([
        { label: 'Relationships', to: '/relationships' },
        { label: name, to: `/relationships/${id}` },
        { label: 'Max Funding Calculator' },
      ]);
    } else {
      setBreadcrumbItems(null);
    }

    setHideBreadcrumbBack(inMaxFundingScenario);

    return () => {
      setBreadcrumbTitle(null);
      setBreadcrumbItems(null);
      setHideBreadcrumbBack(false);
    };
  }, [
    relationship?.relationshipName,
    relationship?.profileName,
    activeTab,
    searchParams,
    id,
    setBreadcrumbTitle,
    setBreadcrumbItems,
    setHideBreadcrumbBack,
  ]);

  const loadRelationship = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      if (isSupport) {
        const data = await supportApi.getProfile(id);
        setRelationship({
          id: data.id,
          profileName: data.profileName,
          profileType: data.profileType as 'SOLO' | 'JOINT' | 'GROUP' | undefined,
          status: data.status as 'active' | 'inactive' | 'archived',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          assignedToId: data.assignedToId,
          assignedToName: data.assignedToName,
          brokerageId: '',
          createdById: data.assignedToId,
          primaryClientId: null,
          relationshipType: (data.profileType as 'SOLO' | 'JOINT' | 'GROUP') || 'SOLO',
          createdBy: '',
          updatedBy: '',
        } as Relationship);
      } else {
        const data = await relationshipApi.getRelationship(id);
        // The API returns the type as `profileType`; normalise it onto
        // `relationshipType` so the rest of the page (type badge, SOLO→JOINT
        // conversion flow) can rely on a single field.
        setRelationship(
          data
            ? { ...data, relationshipType: (data.relationshipType ?? data.profileType ?? 'SOLO') as RelationshipType }
            : data,
        );
      }
    } catch (err) {
      console.error('Failed to load relationship:', err);
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to load relationship';
      setError(errorMessage || 'Failed to load relationship');
    } finally {
      setLoading(false);
    }
  }, [id, isSupport]);

  const loadRelationshipAggregate = useCallback(async () => {
    if (!id || isSupport) return;
    try {
      const data = await relationshipApi.getRelationshipAggregate(id);
      // Update LOA count from aggregate
      if (data.healthIndicators) {
        setLoaCount((data.healthIndicators.completedLOACount || 0) + (data.healthIndicators.pendingLOACount || 0));
      }
    } catch (err) {
      console.error('Failed to load relationship aggregate:', err);
    }
  }, [id, isSupport]);

  const loadProfileOverrides = useCallback(async () => {
    if (!id || isSupport) return;
    try {
      const data = await getProfileOverrides(id);
      setProfileOverrides(data);
    } catch {
      // Non-critical - silently fail
    }
  }, [id, isSupport]);

  const loadProfileAlerts = useCallback(async () => {
    if (!id || isSupport) return;
    try {
      const data = await dashboardApi.getAlerts({ profileId: id, limit: 50 });
      setProfileAlerts(data.alerts);
    } catch {
      // Alerts-service and Resource Server fallback both failed —
      // use pendingActions from the relationship aggregate as last resort
      try {
        const aggregate = await relationshipApi.getRelationshipAggregate(id);
        if (aggregate.pendingActions?.length) {
          setProfileAlerts(aggregate.pendingActions);
          return;
        }
      } catch { /* ignore */ }
      setProfileAlerts([]);
    }
  }, [id, isSupport]);

  const handleRemoveProfileOverride = async () => {
    if (!id || !removeOverrideConfirm) return;
    const overrideToRemove = removeOverrideConfirm;
    setRemoveOverrideConfirm(null);
    try {
      await deleteOverride(id, overrideToRemove.policyId, overrideToRemove.policySignatoryId);
      loadProfileOverrides();
      loadPolicies(policiesCurrentPage);
    } catch {
      alert('Failed to remove override. Please try again.');
    }
  };

  useEffect(() => {
    if (id && currentAdvisor) {
      loadRelationship();
      loadClients(clientsCurrentPage);
      loadPolicies(policiesCurrentPage);
      loadInvitationSummary();
      loadRelationshipAggregate();
      loadProfileOverrides();
      loadProfileAlerts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentAdvisor, loadRelationship]);

  const handleClientsPageChange = (newPage: number) => {
    setClientsCurrentPage(newPage);
    loadClients(newPage);
  };

  const handlePoliciesPageChange = (newPage: number) => {
    setPoliciesCurrentPage(newPage);
    loadPolicies(newPage);
  };

  const loadClients = useCallback(async (page: number = 0) => {
    if (!id) return;

    try {
      setClientsLoading(true);
      setClientsError(null);

      if (isSupport) {
        const response = await supportApi.getProfileClients(id, page, 10);
        setClients(response.content.map(c => ({
          id: c.id,
          profileId: id,
          brokerageId: '',
          firstName: c.firstName,
          lastName: c.lastName,
          email: c.email,
          phone: c.phoneNumber || '',
          dateOfBirth: c.dateOfBirth || '',
          isPrimary: c.isPrimary,
          status: (c.status?.toLowerCase() || 'prospect') as 'prospect' | 'invited' | 'active' | 'inactive',
          addressLine1: '',
          townCity: '',
          postalCode: '',
          countryCode: '',
          country: '',
          createdBy: '',
          createdAt: '',
          updatedAt: '',
        })));
        setClientsTotalPages(response.totalPages);
      } else {
        const response = await clientApi.getClients(id, page, 10);
        setClients(response.content);
        setClientsTotalPages(response.totalPages);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to load clients';
      setClientsError(errorMessage || 'Failed to load clients');
    } finally {
      setClientsLoading(false);
    }
  }, [id, isSupport]);

  const loadPolicies = useCallback(async (page: number = 0) => {
    if (!id) return;

    try {
      setPoliciesLoading(true);
      setPoliciesError(null);

      if (isSupport) {
        const response = await supportApi.getProfilePolicies(id, page, 10);
        setPolicies(response.content.map(p => ({
          id: p.id,
          policyNumber: p.policyNumber,
          providerId: p.providerId,
          providerName: p.providerName,
          category: p.category,
          policyTypeId: p.policyTypeId,
          policyTypeName: p.policyTypeName,
          status: p.status,
          premiumAmount: p.premiumAmount,
          effectiveDate: p.effectiveDate,
          expiryDate: p.expiryDate,
          policyType: p.policyType,
          providerLogoUrl: p.providerLogoUrl,
          ownershipType: p.ownershipType,
          signatories: p.signatories || [],
        } as Policy)));
        setPoliciesTotalPages(response.totalPages);
        setPoliciesTotalElements(response.totalElements);
      } else {
        const response = await policyApi.getPolicies(id, page, 10);
        setPolicies(response.content);
        setPoliciesTotalPages(response.totalPages);
        setPoliciesTotalElements(response.totalElements);
      }
    } catch (err) {
      console.error('Failed to load policies:', err);
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to load policies';
      setPoliciesError(errorMessage || 'Failed to load policies');
    } finally {
      setPoliciesLoading(false);
    }
  }, [id, isSupport]);

  const loadInvitationSummary = async () => {
    if (!id || isSupport) return;
    try {
      await invitationApi.getInvitationSummary(id);
    } catch (err) {
      console.error('Failed to load invitation summary:', err);
    }
  };

  const handleUpdateRelationship = async (data: RelationshipUpdateDTO) => {
    if (!id) return;

    const updated = await relationshipApi.updateRelationship(id, data);
    setRelationship(updated);
    setShowEditModal(false);
  };

  const handleDeleteRelationship = async () => {
    if (!id) return;

    try {
      await relationshipApi.deleteRelationship(id);
      setShowDeleteConfirm(false);
      navigate('/relationships');
    } catch (err) {
      console.error('Failed to delete relationship:', err);
      alert('Failed to delete relationship. Please try again.');
    }
  };

  const handleArchiveRelationship = async () => {
    if (!id) return;

    try {
      setArchiving(true);
      await relationshipApi.updateRelationship(id, { status: 'archived' });
      setShowArchiveModal(false);
      setShowEditModal(false);
      navigate('/relationships');
    } catch (err) {
      console.error('Failed to archive relationship:', err);
      setArchiving(false);
    }
  };

  // Client management handlers
  const handleAddClient = () => {
    setEditingClient(null);
    setShowClientModal(true);
  };

  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setShowClientModal(true);
  };

  const handleCloseClientModal = () => {
    setShowClientModal(false);
    setEditingClient(null);
  };

  const handleSubmitClient = async (
    data: ClientCreateDTO | ClientUpdateDTO,
    relationshipUpdate?: { relationshipName: string; crmRelationshipNumber?: string },
  ) => {
    if (!id) return;

    try {
      setSubmittingClient(true);
      if (editingClient) {
        await clientApi.updateClient(id, editingClient.id, data as ClientUpdateDTO);
      } else {
        // Adding a client to a SOLO relationship auto-promotes it to JOINT on the
        // backend; when that happens the modal also collects the new relationship
        // name / CRM number, applied here after the client is created.
        await clientApi.createClient(id, data as ClientCreateDTO);
        if (relationshipUpdate) {
          await relationshipApi.updateRelationship(id, relationshipUpdate);
        }
      }
      await loadClients(clientsCurrentPage);
      await loadRelationship();
      handleCloseClientModal();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error submitting client:', error);
      alert(error.response?.data?.message || 'Failed to save client');
    } finally {
      setSubmittingClient(false);
    }
  };

  const handleSetPrimary = async (clientId: string) => {
    if (!id) return;

    try {
      await clientApi.setPrimaryClient(id, clientId);
      await loadClients(clientsCurrentPage);
      await loadRelationship();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error setting primary client:', error);
      alert(error.response?.data?.message || 'Failed to set primary client');
    }
  };

  const confirmArchiveClient = (client: Client) => {
    setArchiveClientConfirm({ open: true, client });
  };

  const handleArchiveClient = async (data: { relationshipName: string; crmRelationshipNumber: string }) => {
    if (!id || !archiveClientConfirm.client) return;

    try {
      setArchivingClient(true);
      // Archiving a secondary client converts a joint relationship to solo: remove the client,
      // flip the type to SOLO, then apply the advisor's updated name / CRM number.
      await clientApi.deleteClient(id, archiveClientConfirm.client.id);
      if (relationship && relationship.relationshipType !== 'SOLO') {
        await relationshipApi.changeRelationshipType(id, { newRelationshipType: 'SOLO' });
      }
      await relationshipApi.updateRelationship(id, {
        relationshipName: data.relationshipName,
        crmRelationshipNumber: data.crmRelationshipNumber,
      });
      await loadClients(clientsCurrentPage);
      await loadRelationship();
      setArchiveClientConfirm({ open: false, client: null });
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error archiving client:', error);
      alert(error.response?.data?.message || 'Failed to archive client.');
      setArchiveClientConfirm({ open: false, client: null });
    } finally {
      setArchivingClient(false);
    }
  };

  // Policy management handlers
  const handleAddPolicy = () => {
    setEditingPolicy(null);
    setShowPolicyModal(true);
  };

  const handleEditPolicy = (policy: Policy) => {
    setEditingPolicy(policy);
    setShowPolicyModal(true);
  };

  const handleClosePolicyModal = () => {
    setShowPolicyModal(false);
    setEditingPolicy(null);
  };

  const handleSubmitPolicy = async (data: PolicyCreateDTO | PolicyUpdateDTO) => {
    if (!id) return;

    try {
      setSubmittingPolicy(true);
      if (editingPolicy) {
        await policyApi.updatePolicy(id, editingPolicy.id, data as PolicyUpdateDTO);
      } else {
        const createData = data as PolicyCreateDTO;
        // Auto-assign signatories based on ownership type
        if (!createData.signatoryClientIds && clients.length > 0) {
          if (!createData.ownershipType || createData.ownershipType === 'SOLO') {
            // SOLO: assign the primary client (or first client)
            const primaryClient = clients.find(c => c.isPrimary) || clients[0];
            createData.signatoryClientIds = [primaryClient.id];
          } else {
            // JOINT/GROUP: assign all clients
            createData.signatoryClientIds = clients.map(c => c.id);
          }
        }
        await policyApi.createPolicy(id, createData);
      }
      await loadPolicies(policiesCurrentPage);
      handleClosePolicyModal();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error submitting policy:', error);
      alert(error.response?.data?.message || 'Failed to save policy');
    } finally {
      setSubmittingPolicy(false);
    }
  };

  const handleDeletePolicy = (policyId: string) => {
    setDeletingPolicyId(policyId);
  };

  const handleManageClientRelationships = (policy: Policy) => {
    setSelectedPolicy(policy);
    setShowSignatoriesModal(true);
  };

  const handleCloseSignatoriesModal = () => {
    setShowSignatoriesModal(false);
    setSelectedPolicy(null);
  };

  // Reassignment handlers
  const handleReassignSuccess = (updatedRelationship: Relationship) => {
    setRelationship(updatedRelationship);
    setShowReassignModal(false);
  };

  // Helper functions
  const formatStatus = (status: string | undefined): string => {
    if (!status) return 'Active';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getStatusTagClass = (status: string | undefined): string => {
    const s = (status || 'active').toLowerCase();
    if (s === 'inactive') return 'relationship-header__tag relationship-header__tag--status-inactive';
    if (s === 'archived') return 'relationship-header__tag relationship-header__tag--status-archived';
    return 'relationship-header__tag relationship-header__tag--status';
  };

  const formatRelationshipType = (type: string | undefined): string => {
    if (!type) return 'Solo';
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  };

  if (loading) {
    return (
      <div className="relationship-detail-page">
        <div className="relationship-loading">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relationship-detail-page">
        <Alert variant="error" header="Error Loading Relationship">
          <p>{error}</p>
          <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
        </Alert>
      </div>
    );
  }

  if (!relationship) {
    return (
      <div className="relationship-detail-page">
        <Alert variant="warning" header="Relationship Not Found">
          <p>The requested relationship could not be found.</p>
          <Button onClick={() => navigate('/')}>Back to Dashboard</Button>
        </Alert>
      </div>
    );
  }

  const relationshipName = relationship.relationshipName || relationship.profileName || 'Unnamed Relationship';

  return (
    <UnsavedChangesProvider>
    <div className="flex flex-col">
      {/* Support Mode Banner */}
      {isSupport && (
        <div className="support-mode-banner">
          <AppIcon name="headphones outline" size={24} className="support-mode-banner__icon" />
          <div className="support-mode-banner__content">
            <div className="support-mode-banner__title">Support Mode</div>
            <p className="support-mode-banner__text">
              You are viewing this profile on behalf of <strong>{relationship?.assignedToName}</strong>.
              You can view details and send reminders, but cannot edit the profile or manage LOAs.
            </p>
          </div>
        </div>
      )}

      {/* Combined Header + Tab Card.
          Hidden while inside a Max Funding scenario editor (both existing and new)
          so the scenario page can use the full canvas — its own header carries the
          back button + actions. */}
      {!(
        activeTab === 'tools' &&
        searchParams.get('tool') === 'max-funding' &&
        (searchParams.get('view') === 'scenario' || searchParams.get('view') === 'new')
      ) && (
      <div className="bg-white flex flex-col px-lg pt-lg">
        <div className="flex w-full">
          <div className="flex w-full flex-col">
            <div className="flex items-center gap-sm">
              <div className="font-sans !text-h5 !font-medium leading-normal !pb-[6px] !m-0">{relationshipName}</div>
              {id && <CopyTagButton entityKind="Relationship" entityId={id} />}
            </div>
            {relationship.crmRelationshipNumber && (
              <div className="font-sans text-body-02 font-normal leading-normal text-grey-04">CRM #{relationship.crmRelationshipNumber}</div>
            )}
            {id && (
              <div className="!mb-lg mt-sm">
                <EntityTagEditor entityType="RELATIONSHIP" entityId={id} tags={relationshipTags} />
              </div>
            )}
          </div>
          <div className="relationship-header__actions">
            <div className={getStatusTagClass(relationship.status)}>
              <AppIcon name="checkbox" size={16} className="relationship-header__tag-icon" />
              {formatStatus(relationship.status)}
            </div>
            <div className="relationship-header__tag relationship-header__tag--type">
              {formatRelationshipType(relationship.relationshipType)}
            </div>
            {!isSupport && (
              <RelationshipActionMenu
                onEdit={() => setShowEditModal(true)}
                onChangeOfAgency={
                  COA_ENABLED_TENANTS.has(getTenant())
                    ? () => navigate(`/relationships/${relationship.id}/change-of-agency`)
                    : undefined
                }
                onProjectionAssumptions={() => setShowAssumptions(true)}
              />
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <GatedTabBar
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'clients', label: 'Client Info' },
            { key: 'policies', label: 'Policies', count: policiesTotalElements },
            { key: 'requests', label: 'Requests', count: requestsCount },
            { key: 'proposals', label: 'Proposals', count: proposalsCount },
            { key: 'signed-documents', label: 'Documents', count: loaCount },
            { key: 'fact-find', label: 'Fact Find' },
            { key: 'financial-position', label: 'Financial Position' },
            ...(featureFlags.cashflowWorkspace ? [{ key: 'cashflow', label: 'Cashflow' }] : []),
            { key: 'activity', label: 'Activity' },
            { key: 'notes', label: 'Notes' },
          ]}
          activeTab={activeTab}
          onTabChange={(key) => setActiveTab(key as TabType)}
          renderBefore={
            !isSupport
              ? {
                  key: 'signed-documents',
                  node: (
                    <GatedToolsMenu
                      onSelectTool={(tool: ToolKey) => {
                        if (tool === 'max-funding') {
                          const next = new URLSearchParams(searchParams);
                          next.set('tab', 'tools');
                          next.set('tool', tool);
                          next.delete('view');
                          next.delete('scenarioId');
                          next.delete('clientId');
                          setSearchParams(next, { replace: false });
                        }
                        // Other tools are placeholders; clicks blocked inside ToolsMenu.
                      }}
                    />
                  ),
                }
              : undefined
          }
        />
      </div>
      )}

      <div
        className={`bg-page-bg min-h-[calc(100vh-80px)] overflow-auto ${
          activeTab === 'tools' &&
          searchParams.get('tool') === 'max-funding' &&
          (searchParams.get('view') === 'scenario' || searchParams.get('view') === 'new')
            ? 'p-0'
            : 'p-lg'
        }`}
      >
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Summary (2) + Suggested Steps (1) side by side on wide screens; stacked below lg. */}
            {id && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-md items-start">
                <div className="lg:col-span-2 min-w-0">
                  <ExecutiveSummaryCard profileId={id} />
                </div>
                <div className="lg:col-span-1 min-w-0">
                  <SuggestedStepsPanel profileId={id} />
                </div>
              </div>
            )}
            {/* Open Items checklist (derived + manual) */}
            {id && <OpenItemsPanel profileId={id} />}
            {/* Alerts / Insights Card */}
            <div className="overview-alerts-card">
              <div className="overview-alerts-card__header">
                <div className="inline-flex items-center rounded-full bg-white gap-xs" style={{ border: '1.5px solid #BDB8FF' }}>
                  <button
                    className={`flex items-center justify-center gap-sm w-[101px] h-[36px] rounded-full border-none cursor-pointer transition-all duration-200 ${overviewMode === 'alerts' ? 'bg-lilac-05' : 'bg-white'}`}
                    onClick={() => setOverviewMode('alerts')}
                  >
                    <span className="text-body-02 font-medium text-purple-primary font-sans">Alerts</span>
                    <span className="flex items-center justify-center w-4 h-4 rounded-full bg-purple-primary text-white text-[8px] font-normal leading-normal">
                      {profileAlerts.length}
                    </span>
                  </button>
                  <button
                    className={`flex items-center justify-center gap-sm w-[101px] h-[36px] rounded-full border-none cursor-pointer transition-all duration-200 ${overviewMode === 'insights' ? 'bg-lilac-05' : 'bg-white'}`}
                    onClick={() => setOverviewMode('insights')}
                  >
                    <span className="text-body-02 font-medium text-purple-primary font-sans">Insights</span>
                    <span className="flex items-center justify-center w-4 h-4 rounded-full bg-purple-primary text-white text-[8px] font-normal leading-normal">0</span>
                  </button>
                </div>
                {overviewMode === 'alerts' && profileAlerts.length > 0 && (
                  <button
                    onClick={() => navigate('/activity-log')}
                    className="text-black text-body-03 font-medium leading-normal capitalize font-sans hover:underline cursor-pointer bg-transparent border-none p-0"
                  >
                    View All
                  </button>
                )}
              </div>
              <div className="overview-alerts-card__list">
                {overviewMode === 'alerts' && (
                  <>
                    {profileAlerts.length > 0 ? (
                      profileAlerts.slice(0, 5).map((alert, idx) => (
                        <div key={alert.id || idx} className="overview-alerts-card__item">
                          <div className="overview-alerts-card__item-icon" style={{
                            background:
                              (alert.type === 'INCOMPLETE_PROFILE' || alert.type === 'INCOMPLETE_PROFILE_EXTENDED' || alert.severity === 'URGENT')
                                ? '#FEEEEE'
                                : alert.severity === 'WARNING'
                                ? '#F9F6D3'
                                : '#EAFAEF',
                          }}>
                            {(alert.type === 'INCOMPLETE_PROFILE' || alert.type === 'INCOMPLETE_PROFILE_EXTENDED' || alert.severity === 'URGENT') ? (
                              <AppIcon name="severity warning" size={18} color="#96040E" />
                            ) : alert.severity === 'WARNING' ? (
                              <AppIcon name="severity exclamation" size={18} color="#733F01" />
                            ) : (
                              <AppIcon name="severity check" size={18} color="#004E24" />
                            )}
                          </div>
                          <div className="overview-alerts-card__item-content" style={{ flex: 1 }}>
                            <div className="overview-alerts-card__item-header">
                              <span className="overview-alerts-card__item-title">{alert.title}</span>
                            </div>
                            <div style={{ fontSize: '14px', color: '#333', marginTop: '2px' }}>{alert.description}</div>
                          </div>
                          {alert.eventTimestamp && (
                            <div style={{ fontSize: '12px', color: '#333', whiteSpace: 'nowrap', flexShrink: 0, paddingTop: '2px' }}>
                              {(() => {
                                const diffMs = Date.now() - new Date(alert.eventTimestamp).getTime();
                                const diffMins = Math.floor(diffMs / 60000);
                                const diffHours = Math.floor(diffMs / 3600000);
                                const diffDays = Math.floor(diffMs / 86400000);
                                if (diffMins < 1) return 'just now';
                                if (diffMins < 60) return `${diffMins} mins ago`;
                                if (diffHours < 24) return `${diffHours} hours ago`;
                                if (diffDays === 1) return 'yesterday';
                                return `${diffDays} days ago`;
                              })()}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center justify-center gap-[10px] self-stretch rounded-card bg-lilac-06 px-4 py-10">
                        <span className="text-body-02 text-black font-normal leading-normal">No alerts at this time.</span>
                      </div>
                    )}
                  </>
                )}
                {overviewMode === 'insights' && (
                  <div className="flex items-center justify-center gap-[10px] self-stretch rounded-card bg-lilac-06 px-4 py-10">
                    <span className="text-body-02 text-black font-normal leading-normal">No insights available yet.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Financial Position dashboard (projected from the Fact-Find) */}
            {id && (
              <FinancialDashboard
                profileId={id}
                refreshSignal={fpRefresh}
                onChanged={() => setFpRefresh((k) => k + 1)}
              />
            )}

            {/* Relationship cashflow + wealth (20-year projection). Assumptions are
                edited from the relationship actions ("…") menu → Projection assumptions.
                Shares fpRefresh so an income change refetches it alongside the tiles. */}
            {id && <RelationshipCashflowCard profileId={id} refreshSignal={fpRefresh} />}

            {/* Stats Bar */}
            <div className="flex bg-white px-md py-lg gap-lg rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)]">
              <div className="overview-stats-bar__item">
                <div className="text-black text-body-03 font-normal leading-normal font-sans">Created by</div>
                <div className="text-black text-body-02 font-medium leading-normal font-sans">
                  {relationship.createdByName || relationship.createdBy || '-'}
                </div>
              </div>
              <div className="w-[1px] h-[46px] bg-[#E1E1E1]" />
              <div className="overview-stats-bar__item">
                <div className="text-black text-body-03 font-normal leading-normal font-sans">Assigned to</div>
                <div className="text-black text-body-02 font-medium leading-normal font-sans">
                  {relationship.assignedToName || relationship.assignedToId || '-'}
                  {isAdmin && !isSupport && (
                    <Button
                      variant="outlined"
                      size="sm"
                      style={{ marginLeft: '0.5em' }}
                      onClick={() => setShowReassignModal(true)}
                    >
                      Reassign
                    </Button>
                  )}
                </div>
              </div>
              <div className="w-[1px] h-[46px] bg-[#E1E1E1]" />
              <div className="overview-stats-bar__item">
              <div className="text-black text-body-03 font-normal leading-normal font-sans">Created on</div>
                <div className="text-black text-body-02 font-medium leading-normal font-sans">
                  {relationship.createdAt ? new Date(relationship.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="flex flex-col">
            <ClientList
              clients={clients}
              loading={clientsLoading}
              error={clientsError}
              onEdit={handleEditClient}
              onArchive={confirmArchiveClient}
              onSetPrimary={handleSetPrimary}
              onInvitationUpdate={loadInvitationSummary}
              onRowClick={(client) => navigate(`/clients/${client.id}`)}
              readOnly={isSupport}
            />

            {clientsTotalPages > 1 && (
              <TablePagination
                config={{
                  currentPage: clientsCurrentPage + 1,
                  totalPages: clientsTotalPages,
                  itemsPerPage: 10,
                  itemsPerPageOptions: [5, 10, 20],
                  onPageChange: (next) => handleClientsPageChange(next - 1),
                }}
              />
            )}

            {!isSupport && (
              <div className="flex justify-end mt-lg">
                <Button
                  variant="filled"
                  size="lg"
                  onClick={handleAddClient}
                  disabled={clients.length >= 2}
                >
                  Add Client
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Policies Tab */}
        {activeTab === 'policies' && (
          <Card>
            {/* Header: title + search + add button */}
            <div className="flex flex-col items-start gap-md py-lg px-md">
              <div className="font-sans text-body-01 font-medium leading-normal text-black !m-0">Policies</div>
              <div className="flex items-center justify-between gap-2 self-stretch">
                <div className="flex-1 max-w-[700px]">
                  <SearchBar
                    value={policySearchQuery}
                    onChange={setPolicySearchQuery}
                    placeholder="Search by provider or policy type"
                  />
                </div>
                {!isSupport && (
                  <div className="flex items-center gap-2">
                    {(signatoryStatusSummary?.draft ?? 0) > 0 && (
                      <FinButton variant="outlined" size="sm" onClick={() => setShowBulkPublishModal(true)}>
                        <AppIcon name="send" /> Publish Draft ({signatoryStatusSummary?.draft})
                      </FinButton>
                    )}
                    <FinButton variant="filled" size="lg" onClick={handleAddPolicy}>
                      Add Policy
                    </FinButton>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white">
              <PolicyList
                policies={policySearchQuery
                  ? policies.filter(p => {
                      const q = policySearchQuery.toLowerCase();
                      return (p.providerName || p.carrier || '').toLowerCase().includes(q)
                        || (p.policyTypeName || p.category || '').toLowerCase().includes(q);
                    })
                  : policies}
                loading={policiesLoading}
                error={policiesError}
                onEdit={handleEditPolicy}
                onDelete={handleDeletePolicy}
                onManageRelationships={handleManageClientRelationships}
                onRowClick={(policy) => navigate(`/policies/${policy.id}`)}
                readOnly={isSupport}
                policyIdsWithOverrides={profileOverrides ? new Set(profileOverrides.overrides.map(o => o.policyId)) : undefined}
              />
            </div>

            {policiesTotalPages > 1 && (
              <TablePagination
                config={{
                  currentPage: policiesCurrentPage + 1,
                  totalPages: policiesTotalPages,
                  itemsPerPage: 10,
                  itemsPerPageOptions: [5, 10, 20],
                  onPageChange: (next) => handlePoliciesPageChange(next - 1),
                }}
              />
            )}
          </Card>
        )}

        {/* Client Signed Documents Tab (unified LOAs + CoAs) + relationship
            email correspondence (auto-linked / tagged Gmail metadata) */}
        {activeTab === 'signed-documents' && (
          <>
            <ClientSignedDocumentsList
              profileId={relationship.id}
              readOnly={isSupport}
            />
            <RelationshipEmailsCard
              profileId={relationship.id}
              readOnly={isSupport}
            />
          </>
        )}

        {/* Profile Requests Tab (all Request types — signing + non-signing) */}
        {activeTab === 'requests' && (
          <RequestsTab
            profileId={relationship.id}
            policies={policies}
            onCountChange={setRequestsCount}
          />
        )}

        {activeTab === 'proposals' && (
          <ProposalsTab
            profileId={relationship.id}
            brokerageId={relationship.brokerageId}
            clients={clients}
            onCountChange={setProposalsCount}
          />
        )}

        {/* Fact Find Tab — editable by the assigned advisor (collapsible per-section
            boxes); shares the live draft with the client. */}
        {activeTab === 'fact-find' && <FactFindAccordion profileId={relationship.id} />}
        {activeTab === 'financial-position' && (
          <>
            <FinancialPositionTab profileId={relationship.id} />
            <FinancialGoalsPanel profileId={relationship.id} />
            <TaxClassificationPanel profileId={relationship.id} />
            <InheritancesPanel profileId={relationship.id} />
          </>
        )}
        {/* Cashflow Modelling Tab — forecast list + workspace entry (PRD R1–R4). */}
        {activeTab === 'cashflow' && featureFlags.cashflowWorkspace && (
          <CashflowForecastTab profileId={relationship.id} />
        )}
        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <RelationshipNotesTab profileId={relationship.id} />
        )}
        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <ClientActivityTab profileId={relationship.id} />
        )}
        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <RelationshipToolsTab
            profileId={relationship.id}
            brokerageId={relationship.brokerageId}
            clients={clients}
          />
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <RelationshipEditModal
          relationship={relationship}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateRelationship}
          onArchive={() => {
            setShowEditModal(false);
            setShowArchiveModal(true);
          }}
        />
      )}

      {/* Projection assumptions (relationship-level override) — opened from the "…" menu */}
      {id && (
        <RelationshipAssumptionsDialog
          profileId={id}
          open={showAssumptions}
          onClose={() => setShowAssumptions(false)}
        />
      )}

      {/* Archive Confirmation Modal */}
      <ArchiveRelationshipModal
        open={showArchiveModal}
        relationshipName={relationshipName}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchiveRelationship}
        loading={archiving}
      />

      {/* Client Modal */}
      <FinModal open={showClientModal} onClose={handleCloseClientModal} size="large">
        <FinModal.Content className="!p-0">
          <ClientForm
            client={editingClient}
            primaryClient={clients.find((c) => c.isPrimary) ?? null}
            relationshipType={relationship?.relationshipType}
            onSubmit={handleSubmitClient}
            onCancel={handleCloseClientModal}
            loading={submittingClient}
          />
        </FinModal.Content>
      </FinModal>

      {/* Archive Client Confirmation Modal */}
      <ArchiveClientModal
        open={archiveClientConfirm.open}
        onClose={() => setArchiveClientConfirm({ open: false, client: null })}
        onConfirm={handleArchiveClient}
        loading={archivingClient}
      />

      {/* Delete Relationship Confirmation Modal */}
      <FinModal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} size="small" variant="warning">
        <FinModal.Header title="Archive Relationship" onClose={() => setShowDeleteConfirm(false)} />
        <FinModal.Content>
          <p>
            Are you sure you want to archive the relationship{' '}
            <strong>{relationshipName}</strong>?
          </p>
          <p>
            This will change the relationship status to &quot;archived&quot; and it will no longer appear
            in the active relationships list.
          </p>
        </FinModal.Content>
        <FinModal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setShowDeleteConfirm(false) }}
          primaryAction={{ label: 'Archive Relationship', onClick: () => { void handleDeleteRelationship(); }, colour: 'red' }}
        />
      </FinModal>

      {/* Policy Modal */}
      <FinModal open={showPolicyModal} onClose={handleClosePolicyModal} size="medium">
        <FinModal.Header
          title={editingPolicy ? 'Edit Policy' : 'Add New Policy'}
          onClose={handleClosePolicyModal}
        />
        <FinModal.Content>
          <PolicyForm
            policy={editingPolicy || undefined}
            onSubmit={handleSubmitPolicy}
            onCancel={handleClosePolicyModal}
            loading={submittingPolicy}
            error={null}
          />
        </FinModal.Content>
      </FinModal>

      {/* Policy Signatories Modal */}
      {showSignatoriesModal && selectedPolicy && (
        <PolicySignatories
          policy={selectedPolicy}
          clients={clients}
          onClose={handleCloseSignatoriesModal}
          onUpdate={() => loadPolicies(policiesCurrentPage)}
        />
      )}

      {/* Delete Policy Modal */}
      {deletingPolicyId && id && (
        <DeletePolicyModal
          open={!!deletingPolicyId}
          profileId={id}
          policyId={deletingPolicyId}
          onClose={() => setDeletingPolicyId(null)}
          onSuccess={() => {
            setDeletingPolicyId(null);
            loadPolicies(policiesCurrentPage);
          }}
        />
      )}

      {/* Bulk Invitation Modal */}
      {showBulkInvitationModal && id && relationship && (
        <SendInvitationModal
          profileId={id}
          profileName={relationshipName}
          totalClients={clients.length}
          open={showBulkInvitationModal}
          onClose={() => setShowBulkInvitationModal(false)}
          onSuccess={() => {
            loadInvitationSummary();
            loadClients(clientsCurrentPage);
          }}
        />
      )}

      {/* Reassign Relationship Modal */}
      <ReassignRelationshipModal
        open={showReassignModal}
        relationship={relationship}
        currentAdvisorId={relationship?.assignedToId}
        onClose={() => setShowReassignModal(false)}
        onSuccess={handleReassignSuccess}
      />

      {/* Bulk Publish Modal */}
      {showBulkPublishModal && id && (
        <BulkPublishConfirmModal
          open={showBulkPublishModal}
          onClose={() => setShowBulkPublishModal(false)}
          onSuccess={() => {
            refreshSignatoryStatus();
            loadPolicies(policiesCurrentPage);
          }}
          profileId={id}
          draftCount={signatoryStatusSummary?.draft || 0}
        />
      )}

      {/* Remove Override Confirmation */}
      <FinModal open={!!removeOverrideConfirm} onClose={() => setRemoveOverrideConfirm(null)} size="small" variant="warning">
        <FinModal.Header title="Remove Detail Override" onClose={() => setRemoveOverrideConfirm(null)} />
        <FinModal.Content>
          <p>
            {removeOverrideConfirm
              ? `Are you sure you want to remove the detail override for ${removeOverrideConfirm.clientName} on policy ${removeOverrideConfirm.policyNumber}? Future LOAs will use the client's current profile details.`
              : ''}
          </p>
        </FinModal.Content>
        <FinModal.Actions
          secondaryAction={{ label: 'Cancel', onClick: () => setRemoveOverrideConfirm(null) }}
          primaryAction={{ label: 'Remove Override', onClick: () => { void handleRemoveProfileOverride(); }, colour: 'red' }}
        />
      </FinModal>
    </div>
    </UnsavedChangesProvider>
  );
}

function GatedTabBar(props: React.ComponentProps<typeof TabBar>) {
  const unsaved = useUnsavedChangesOptional();
  const handleChange = (key: string) => {
    if (unsaved?.isDirty) {
      unsaved.requestNavigation(() => props.onTabChange(key));
    } else {
      props.onTabChange(key);
    }
  };
  return <TabBar {...props} onTabChange={handleChange} />;
}

function GatedToolsMenu({ onSelectTool }: Readonly<{ onSelectTool: (tool: ToolKey) => void }>) {
  const unsaved = useUnsavedChangesOptional();
  const handle = (tool: ToolKey) => {
    if (unsaved?.isDirty) {
      unsaved.requestNavigation(() => onSelectTool(tool));
    } else {
      onSelectTool(tool);
    }
  };
  return <ToolsMenu onSelectTool={handle} />;
}
